(() => {
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.media = [...this.querySelectorAll('[data-product-media]')];
      this.thumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
      this.dialog = this.querySelector('[data-gallery-dialog]');
      this.dialogContent = this.querySelector('[data-gallery-dialog-content]');
      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onTouchStart = this.handleTouchStart.bind(this);
      this.onTouchEnd = this.handleTouchEnd.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.configureArrows();
      const selected = this.querySelector('[data-gallery-thumbnail][aria-current="true"]')?.dataset.mediaId || this.media[0]?.dataset.mediaId;
      if (selected) this.selectMedia(String(selected), { focus: false });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('touchstart', this.onTouchStart);
      this.removeEventListener('touchend', this.onTouchEnd);
    }

    configureArrows() {
      const rtl = document.documentElement.dir === 'rtl' || getComputedStyle(this).direction === 'rtl';
      const previous = this.querySelector('[data-gallery-previous]');
      const next = this.querySelector('[data-gallery-next]');
      const dialogPrevious = this.querySelector('[data-gallery-dialog-previous]');
      const dialogNext = this.querySelector('[data-gallery-dialog-next]');
      if (previous) previous.textContent = rtl ? '→' : '←';
      if (next) next.textContent = rtl ? '←' : '→';
      if (dialogPrevious) dialogPrevious.textContent = rtl ? '→' : '←';
      if (dialogNext) dialogNext.textContent = rtl ? '←' : '→';
    }

    selectMedia(mediaId, { focus = false } = {}) {
      const selected = String(mediaId);
      this.dataset.selectedMediaId = selected;
      this.media.forEach((item) => {
        const active = item.dataset.mediaId === selected;
        item.hidden = !active;
        if (!active) this.pauseMedia(item);
      });
      this.thumbnails.forEach((thumbnail) => {
        const active = thumbnail.dataset.mediaId === selected;
        thumbnail.setAttribute('aria-current', String(active));
        if (active && focus) thumbnail.focus();
      });
      this.dispatchEvent(new CustomEvent('media:change', { bubbles: true, detail: { mediaId: selected } }));
    }

    index() { return this.thumbnails.findIndex((item) => item.dataset.mediaId === this.dataset.selectedMediaId); }
    move(direction, { focus = true } = {}) {
      if (!this.thumbnails.length) return;
      const next = (this.index() + direction + this.thumbnails.length) % this.thumbnails.length;
      this.selectMedia(this.thumbnails[next].dataset.mediaId, { focus });
      if (this.dialog?.open) this.renderDialogMedia();
    }

    pauseMedia(item) {
      item.querySelector('video')?.pause?.();
      item.querySelector('model-viewer')?.pause?.();
      item.querySelectorAll('iframe').forEach((frame) => {
        try { frame.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'); frame.contentWindow?.postMessage('{"method":"pause"}', '*'); } catch { /* Cross-origin embeds remain safely isolated. */ }
      });
    }

    renderDialogMedia() {
      const current = this.media.find((item) => item.dataset.mediaId === this.dataset.selectedMediaId);
      const visual = current?.querySelector('img, video, model-viewer');
      if (!visual || !this.dialogContent) return;
      this.dialogContent.replaceChildren(visual.cloneNode(true));
    }

    openDialog(trigger) {
      if (!this.dialog) return;
      this.dialogTrigger = trigger || document.activeElement;
      this.renderDialogMedia();
      this.dialog.showModal?.();
    }
    closeDialog() { this.dialog?.close?.(); if (this.dialogTrigger?.isConnected) this.dialogTrigger.focus(); }

    handleClick(event) {
      const thumbnail = event.target.closest('[data-gallery-thumbnail]');
      if (thumbnail) this.selectMedia(thumbnail.dataset.mediaId, { focus: false });
      if (event.target.closest('[data-gallery-dialog-next]')) this.move(1, { focus: false });
      else if (event.target.closest('[data-gallery-next]')) this.move(1);
      if (event.target.closest('[data-gallery-dialog-previous]')) this.move(-1, { focus: false });
      else if (event.target.closest('[data-gallery-previous]')) this.move(-1);
      const zoom = event.target.closest('[data-gallery-zoom]');
      if (zoom) this.openDialog(zoom);
      if (event.target.closest('[data-gallery-dialog-close]')) this.closeDialog();
    }

    handleKeydown(event) {
      const rtl = document.documentElement.dir === 'rtl' || getComputedStyle(this).direction === 'rtl';
      const focus = !this.dialog?.open;
      if (event.key === 'ArrowRight') { event.preventDefault(); this.move(rtl ? -1 : 1, { focus }); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.move(rtl ? 1 : -1, { focus }); }
      if (event.key === 'Escape' && this.dialog?.open) this.closeDialog();
    }

    handleTouchStart(event) { this.touchStartX = event.changedTouches?.[0]?.screenX; }
    handleTouchEnd(event) {
      const end = event.changedTouches?.[0]?.screenX;
      if (typeof this.touchStartX !== 'number' || typeof end !== 'number') return;
      const delta = end - this.touchStartX;
      if (Math.abs(delta) > 40) {
        const rtl = document.documentElement.dir === 'rtl' || getComputedStyle(this).direction === 'rtl';
        this.move(delta > 0 ? (rtl ? 1 : -1) : (rtl ? -1 : 1));
      }
      this.touchStartX = undefined;
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
})();
